// Waiting — questions belonging to OTHER people (console-personas.md §4.3).
// LIST, not a stage. Data: Addendum B per hub-linked company.

import {
  AdminApiError,
  HUB_QUESTIONS_LIST_LIMIT,
  hubQuestionsListCapFired,
  type AdminClient,
} from "../api.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text";
import { clear, h, safeText } from "../dom.js";
import type { AdminMonthsResponse } from "../console/types.js";
import type {
  AdminHubIntakeCounts,
  AdminHubQuestionRow,
  Company,
  CompanySlug,
  StaffRole,
} from "../types.js";
import { mapWithConcurrency } from "../ui/concurrency.js";
import { moneyText } from "../ui/display-words.js";
import * as face from "../ui/face.js";
import { readerRole } from "../ui/facts.js";
import { failureCopy, outcomeSentence } from "../ui/failure.js";
import { createFreshnessCaption } from "../ui/freshness.js";
import { navLabel } from "../ui/sentences.js";
import { runWrite } from "../ui/write.js";
import { periodNotAfterUtc } from "../ui/period-control.js";
import {
  measuredEmptyCopyFromSet,
  measuredSetOf,
  type MeasuredSet,
} from "../ui/measured-claim.js";
import { button, disclosure, pageHeader, svgIcon } from "../ui/components.js";
import { hubIntakeCountsArrived } from "../ui/hub-intake-words.js";
import { canReassign, humanizeRole, seedRoleOptions } from "../ui/roles.js";

export { HUB_QUESTIONS_LIST_LIMIT, hubQuestionsListCapFired };

/** Fan-out concurrency for hub-question fetches (condition 7). */
const FANOUT_CONCURRENCY = 4;

export interface WaitingPageOptions {
  client: AdminClient;
  root: HTMLElement;
  isCurrent?: () => boolean;
  /** The role the shell already knows (B2a-1); never re-read from the bus. */
  role?: StaffRole;
  companyLabels?: Map<string, string>;
  /**
   * A message from the action that just re-read this page. It stays on screen
   * after the list is refreshed, so a refused write still says what happened.
   */
  notice?: string | undefined;
}

type WaitingRow = AdminHubQuestionRow & {
  company: CompanySlug;
  knownRoles: string[];
};

export async function renderWaitingPage(opts: WaitingPageOptions): Promise<void> {
  const { client, root } = opts;
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  // The shell passes the role it already knows (B2a-1). Without one, owner
  // decision 10 applies: unknown reads as the account manager.
  const role: StaffRole = readerRole(opts.role);
  clear(root);
  root.append(h("p", { class: "muted", "data-role": "waiting-loading" }, ["Loading…"]));

  let allCompanies: Company[];
  let companies: Company[];
  try {
    allCompanies = await loadActiveCompanies(client);
    companies = allCompanies.filter((c) => c.hubLink !== null);
  } catch (error) {
    if (!stillCurrent()) return;
    if (
      error instanceof AdminApiError &&
      (error.status === 403 || error.status === 503)
    ) {
      throw error;
    }
    console.error("waiting load failed", error);
    paintWaitingLoadFailure(root, opts, role, error);
    return;
  }
  if (!stillCurrent()) return;

  // Hub questions are what this page lists. Receipts and bills also hold items
  // that are with the hub or with the owner, and those are counted from the
  // same hub-intake read Home and Receipts use, so the page never says
  // "nothing" about a person it did not ask (round eight B3). Both reads run
  // together and the page paints once both have settled.
  const intakePeriod = periodNotAfterUtc(new Date());
  // The Months read starts beside the hub reads, not after them (round 3,
  // MUST-FIX 2): a page's slowest read should not always be the one that
  // arrives last and meets the rate limit first. Its answer is only needed
  // for the empty sentence and the card line below.
  // Both roles read the same company set (the broader one). The owner's older
  // narrower set was not a permission: a card with the hub in a company with no
  // hub link still waits on another person (B2a-5).
  const blockerRead = loadBlockerCounts(client, allCompanies, role, stillCurrent);
  const [results, intakeResults] = await Promise.all([
    mapWithConcurrency(companies, FANOUT_CONCURRENCY, async (company) => {
      try {
        const body = await client.listHubQuestions(company.slug, { status: "open" });
        return { company, body, ok: true as const };
      } catch {
        // Soft-fail per company — no automatic retry (condition 7).
        return { company, body: null, ok: false as const };
      }
    }),
    mapWithConcurrency(allCompanies, FANOUT_CONCURRENCY, async (company) => {
      try {
        const body = await client.listHubIntake(company.slug, {
          period: intakePeriod,
          limit: 0,
        });
        return { company, body, ok: true as const };
      } catch {
        return { company, body: null, ok: false as const };
      }
    }),
  ]);
  if (!stillCurrent()) return;

  const rows: WaitingRow[] = [];
  const failed: CompanySlug[] = [];
  let anyCompanyAtCap = false;
  for (const result of results) {
    if (!result.ok) {
      failed.push(result.company.slug);
      continue;
    }
    if (hubQuestionsListCapFired(result.body.questions.length)) {
      anyCompanyAtCap = true;
    }
    for (const q of result.body.questions) {
      rows.push({
        ...q,
        company: result.company.slug,
        knownRoles: result.body.knownRoles,
      });
    }
  }

  // Global stalest-first (endpoint orders per company; merge by createdAt).
  rows.sort((a, b) => {
    const t = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    if (t !== 0) return t;
    return a.externalRef.localeCompare(b.externalRef);
  });

  // Completeness is part of the value: fan-out failure OR per-company list cap
  // → incomplete MeasuredSet (cannot claim a complete Waiting list).
  const waitingSet: MeasuredSet<WaitingRow> = measuredSetOf(
    rows,
    failed.length === 0 && !anyCompanyAtCap,
  );

  // The one company directory (B2a-1), built from the rows this page must read
  // anyway. An unknown name reads "this company", never the slug.
  const directory = face.createCompanyDirectory(
    allCompanies.map((row) => ({ slug: row.slug, displayName: row.displayName })),
    { complete: true },
  );
  const labelFor = (slug: CompanySlug): string =>
    face.company(slug, directory, "sentence");
  const loadedAt = new Date();
  const otherSources = summariseIntake(intakeResults, labelFor, role);

  // Round 11 (R7-03, R7-04): a card sent to the hub, and (for an account
  // manager) an escalated card with the owner, are waiting on another person
  // even though neither is a hub question. The Months read carries those
  // counts, and it is read on every visit — a page that lists questions must
  // still say what the hub holds, or Months sends the reader here for a line
  // this page never prints (round 2, MUST-FIX 3). The 30 s read cache serves
  // the same read when the person has just been on Home or Months.
  // Only the measured-empty sentence needs the answer before it is written,
  // and that state has no rows to hold back. A listed page paints first and
  // appends the card line when the read lands.
  let blockerCounts: BlockerCounts | null = null;
  if (waitingSet.items.length === 0 && waitingSet.measureComplete) {
    blockerCounts = await blockerRead.catch((error: unknown) => {
      // A thrown read is "not checked", never zero cards (round 3, MUST-FIX 1).
      console.error("waiting card counts failed", error);
      return null;
    });
    if (!stillCurrent()) return;
  }

  clear(root);

  const live = h("div", {
    class: "aria-live",
    "aria-live": "polite",
    "data-role": "waiting-live",
  });
  root.append(live);

  if (opts.notice !== undefined && opts.notice !== "") {
    root.append(
      h("p", {
        class: "banner banner-err",
        role: "status",
        "data-role": "waiting-notice",
      }, [opts.notice]),
    );
  }

  const freshnessHost = h("div", { "data-role": "freshness-host" });
  root.append(freshnessHost);
  freshnessHost.append(
    createFreshnessCaption({
      updatedAt: loadedAt,
      onRefresh: () => {
        // Refresh is not the action that left a notice: the re-read drops it
        // rather than repeating a sentence about a write nobody just made
        // (round 2, MUST-FIX 1).
        void renderWaitingPage({ ...opts, notice: undefined });
      },
    }),
  );

  root.append(
    mountPaperworkHeader(
      "Waiting",
      "waiting-title",
      "Questions that belong to someone else. Oldest first.",
      "waiting-lede",
    ),
  );

  if (failed.length > 0) {
    const failedWords = failed.map((slug) => labelFor(slug)).join(", ");
    root.append(
      h("p", { class: "banner banner-err", role: "status", "data-role": "waiting-partial" }, [
        `Couldn't check ${failedWords}. Use Refresh to try again.`,
      ]),
    );
  }

  if (anyCompanyAtCap) {
    root.append(
      h("p", {
        class: "banner banner-err",
        role: "status",
        "data-role": "waiting-cap-fired",
      }, [
        `Showing up to ${String(HUB_QUESTIONS_LIST_LIMIT)} open questions per company — more may exist. Refresh later for the rest.`,
      ]),
    );
  }

  const listHost = h("div", { "data-role": "waiting-list" });
  root.append(listHost);

  // The card line on a listed page, from the same helper the empty sentence
  // uses (round 3, MUST-FIX 1). A partial or failed read keeps its caveat
  // visible instead of printing a floor as if it were the total.
  const paintCardLine = (counts: BlockerCounts | null): void => {
    const line = cardLine(counts, role);
    if (line.count !== "") {
      listHost.append(
        h("p", { class: "muted", "data-role": "waiting-cards" }, [line.count]),
      );
    }
    if (line.caveat !== "") {
      listHost.append(
        h("p", {
          class: "muted",
          "data-role": "waiting-cards-caveat",
        }, [line.caveat]),
      );
    }
  };

  const announce = (message: string): void => {
    clear(live);
    live.append(document.createTextNode(message));
  };

  const refreshWithNotice = async (notice: string): Promise<void> => {
    await renderWaitingPage({ ...opts, notice });
  };

  const paintList = (): void => {
    clear(listHost);
    if (waitingSet.items.length === 0) {
      // "Nothing" only when every source was read and is empty. Hub questions
      // are one source; what is with the hub or the owner in Receipts and bills
      // is another; a card sent to the hub, or an account manager's escalated
      // card with the owner, is the third (round 11, R7-03 / R7-04). Anything
      // less says what was measured.
      const nothing =
        otherSources.complete &&
        otherSources.withHub === 0 &&
        otherSources.withOwner === 0 &&
        blockerCounts !== null &&
        blockerCounts.complete &&
        blockerCounts.hub === 0 &&
        (role !== "account_manager" || blockerCounts.owner === 0);
      const emptyCopy = measuredEmptyCopyFromSet(waitingSet, {
        emptyCopy: nothing
          ? NOTHING_WAITING_COPY
          : scopedEmptyCopy(otherSources, blockerCounts, role),
        incompleteEmptyCopy:
          "Couldn't finish checking Waiting — some companies did not load. Use Refresh to try again.",
      });
      // Same shape queue.ts forbids (R20C-6 / R21C-2 residual): never
      // `?? "<clear claim>"`. FromSet returns a string for every empty set
      // today; if that ever changes, refuse rather than restore the lie.
      if (emptyCopy === null) {
        return;
      }
      listHost.append(
        h("p", {
          class: "lede empty-state",
          role: "status",
          "data-role": !waitingSet.measureComplete
            ? "waiting-empty-incomplete"
            : nothing
              ? "waiting-empty"
              : "waiting-hub-only",
        }, [emptyCopy]),
      );
      return;
    }

    const list = h("ul", { class: "waiting-list", "data-role": "waiting-rows" });
    for (const row of waitingSet.items) {
      list.append(
        renderWaitingRow({
          row,
          client,
          labelFor,
          announce,
          refreshWithNotice,
          role,
          stillCurrent,
        }),
      );
    }
    listHost.append(list);
    // The other source keeps its line even when a question is listed: it is
    // what Home and Receipts already say about the same month, and dropping it
    // here made the two pages disagree (rd Z4 B9-M4).
    const intake = intakeLineCopy(otherSources, role);
    if (intake !== "") {
      listHost.append(
        h("p", { class: "muted", "data-role": "waiting-intake" }, [intake]),
      );
    }
  };

  paintList();

  // The list is painted; the card line lands when its read does (round 2,
  // MUST-FIX 3). The measured-empty sentence above already carries it.
  if (blockerCounts === null) {
    void blockerRead
      .then((counts) => {
        if (!stillCurrent()) return;
        paintCardLine(counts);
      })
      .catch((error: unknown) => {
        // A thrown read is not zero cards: it is a read that did not happen,
        // and the line says so rather than going silent (round 3, MUST-FIX 1).
        console.error("waiting card counts failed", error);
        if (!stillCurrent()) return;
        paintCardLine(null);
      });
  }
}

const NOTHING_WAITING_COPY = "Nothing is waiting on another person this month.";

interface OtherWaiting {
  /** True when every company's count was read and none may be short. */
  complete: boolean;
  /** One entry per company that has something with the hub or the owner. */
  lines: string[];
  /** Companies whose counts could not be read, or may be short. */
  uncertain: string[];
  /** Every arrival counted with the hub, across companies. */
  withHub: number;
  /** Every arrival counted with the owner (an account manager's view). */
  withOwner: number;
}

interface BlockerCounts {
  /** True when every company's Months read arrived. */
  complete: boolean;
  /** Cards sent to the hub (the Worker's `cards_with_practice`). */
  hub: number;
  /** The account manager's escalated cards with the owner. */
  owner: number;
}

/**
 * The card counts only the Months read knows: a hub question is one kind of
 * thing waiting on someone else, and a review card sent to the hub (or held by
 * the owner) never appears in that list (round 11, R7-03 / R7-04).
 *
 * Reads one company at a time through the same 30 s cache every other Months
 * read uses. A failed read is not zero: the caller then refuses the "nothing"
 * sentence rather than guessing.
 */
async function loadBlockerCounts(
  client: AdminClient,
  companies: readonly Company[],
  role: StaffRole,
  stillCurrent: () => boolean,
): Promise<BlockerCounts> {
  const results = await mapWithConcurrency<Company, AdminMonthsResponse | null>(
    companies,
    FANOUT_CONCURRENCY,
    async (company) => {
      try {
        return await client.getMonths(company.slug);
      } catch {
        return null;
      }
    },
  );
  if (!stillCurrent()) return { complete: false, hub: 0, owner: 0 };

  let complete = true;
  let hub = 0;
  let owner = 0;
  for (const body of results) {
    if (body === null) {
      complete = false;
      continue;
    }
    const blockers = [
      ...body.undatedBlockers,
      ...body.months.flatMap((month) => month.blockers),
    ];
    for (const blocker of blockers) {
      const count = Number.isInteger(blocker.count) && blocker.count > 0 ? blocker.count : 0;
      if (blocker.key === "cards_with_practice") hub += count;
      if (role === "account_manager" && blocker.key === "cards_with_owner") owner += count;
    }
  }
  return { complete, hub, owner };
}

/** Months' own words for the card counts, so the two pages cannot disagree. */
function blockerSentence(counts: BlockerCounts | null, role: StaffRole): string {
  if (counts === null) return "";
  const parts: string[] = [];
  if (counts.hub > 0) {
    parts.push(
      `${String(counts.hub)} ${counts.hub === 1 ? "card is" : "cards are"} with the hub.`,
    );
  }
  if (role === "account_manager" && counts.owner > 0) {
    parts.push(
      `${String(counts.owner)} ${counts.owner === 1 ? "card is" : "cards are"} with the owner.`,
    );
  }
  return parts.join(" ");
}

/** The shared sentence for a card count this page could not finish checking. */
const CARDS_UNCHECKED_COPY =
  "Couldn't check the cards with the hub or the owner. Use Refresh to try again.";

/**
 * The card line in ONE place, for the empty sentence and the listed page
 * (round 3, MUST-FIX 1).
 *
 * A read that never arrived is not zero cards, and a read that lost one
 * company is a floor, not a total: both keep the count that did arrive (the
 * helper returns it separately) and the caveat that says what is missing. The
 * two paths cannot drift because neither composes this sentence itself.
 */
function cardLine(
  counts: BlockerCounts | null,
  role: StaffRole,
): { count: string; caveat: string } {
  return {
    count: blockerSentence(counts, role),
    caveat: counts === null || !counts.complete ? CARDS_UNCHECKED_COPY : "",
  };
}

/** `cardLine` said as one sentence, for the measured-empty copy. */
function cardLineCopy(counts: BlockerCounts | null, role: StaffRole): string {
  const line = cardLine(counts, role);
  return [line.count, line.caveat].filter((part) => part !== "").join(" ");
}

/**
 * What is with someone else in Receipts and bills, from the hub-intake counts.
 * The owner's own items ("with the owner") are not waiting on another person
 * for the owner, so they are counted for the account manager only, the same
 * split the Receipts chips make.
 */
function summariseIntake(
  results: ReadonlyArray<{
    company: Company;
    ok: boolean;
    body: { counts: AdminHubIntakeCounts; countsComplete: boolean } | null;
  }>,
  labelFor: (slug: CompanySlug) => string,
  role: StaffRole,
): OtherWaiting {
  const lines: string[] = [];
  const uncertain: string[] = [];
  let withHubTotal = 0;
  let withOwnerTotal = 0;
  for (const result of results) {
    if (!result.ok || result.body === null || !hubIntakeCountsArrived(result.body.counts)) {
      uncertain.push(labelFor(result.company.slug));
      continue;
    }
    if (!result.body.countsComplete) uncertain.push(labelFor(result.company.slug));
    const withHub = result.body.counts.with_the_practice;
    const withOwner = role === "account_manager" ? result.body.counts.with_the_owner : 0;
    withHubTotal += withHub;
    withOwnerTotal += withOwner;
    const parts: string[] = [];
    if (withHub > 0) parts.push(`${String(withHub)} with the hub`);
    if (withOwner > 0) parts.push(`${String(withOwner)} with the owner`);
    if (parts.length > 0) lines.push(`${labelFor(result.company.slug)}: ${parts.join(", ")}`);
  }
  return {
    complete: uncertain.length === 0,
    lines,
    uncertain,
    withHub: withHubTotal,
    withOwner: withOwnerTotal,
  };
}

/** What the page measured, said as what it measured. */
function scopedEmptyCopy(
  other: OtherWaiting,
  counts: BlockerCounts | null,
  role: StaffRole,
): string {
  const parts: string[] = [];
  // ONE sentence over all three measures (B2a-5). "No hub questions are open."
  // is true only when the question list, the hub-intake counts and the Months
  // card counts are all zero; beside "1 card is with the hub" it read as a
  // contradiction (round-2 MUST-FIX 3 / rc-4 F6).
  const allZero =
    other.complete &&
    other.withHub === 0 &&
    other.withOwner === 0 &&
    counts !== null &&
    counts.complete &&
    counts.hub === 0 &&
    (role !== "account_manager" || counts.owner === 0);
  if (allZero) parts.push("No hub questions are open.");
  const intake = intakeLineCopy(other, role);
  if (intake !== "") parts.push(intake);
  const cards = cardLineCopy(counts, role);
  if (cards !== "") parts.push(cards);
  // A failed hub-question read has its own incomplete sentence and never gets
  // here; this keeps a measured-empty page from returning an empty string.
  return parts.join(" ");
}

/**
 * The Receipts and bills side of Waiting: what is with the hub or with the
 * owner this month, and what could not be checked. It is one sentence-builder
 * because the empty state and the list must never disagree: a question in the
 * list used to drop this line entirely, so Waiting said one thing while Home
 * and Receipts said another (rd Z4 B9-M4).
 */
function intakeLineCopy(other: OtherWaiting, role: StaffRole): string {
  const parts: string[] = [];
  if (other.lines.length > 0) {
    parts.push(`This month in ${navLabel("receipt-shelf")}: ${other.lines.join("; ")}.`);
  }
  if (other.uncertain.length > 0) {
    // The owner is the person reading: "with the owner" would be about them,
    // so the owner's sentence names only the hub (round 11, R7-22c).
    const withWhom =
      role === "account_manager" ? "with the hub or the owner" : "with the hub";
    parts.push(
      `Couldn't check everything that is ${withWhom} for ${other.uncertain.join(", ")}. Use Refresh to try again.`,
    );
  }
  return parts.join(" ");
}

function renderWaitingRow(opts: {
  row: WaitingRow;
  client: AdminClient;
  labelFor: (slug: CompanySlug) => string;
  announce: (message: string) => void;
  role: StaffRole;
  stillCurrent: () => boolean;
  refreshWithNotice: (notice: string) => Promise<void>;
}): HTMLLIElement {
  const { row, client, labelFor, announce, refreshWithNotice, role } = opts;
  const li = h("li", {
    class: "waiting-row",
    "data-role": "waiting-row",
    "data-external-ref": row.externalRef,
    "data-company": row.company,
  });

  const addressee = h(
    "span",
    { class: "waiting-addressee system-label", "data-role": "waiting-addressee" },
    [humanizeRole(row.assignToRole)],
  );

  const companyWords = labelFor(row.company);
  const meta = h("div", { class: "waiting-meta" }, [
    h("span", { class: "waiting-date", "data-role": "waiting-date" }, [
      formatDisplayDate(row.txnDate),
    ]),
    h("span", { class: "waiting-amount money", "data-role": "waiting-amount" }, [
      formatAmount(row.amount),
    ]),
    h("span", { class: "waiting-memo", "data-role": "waiting-memo" }, [
      // Untrusted txn memo (Addendum B); Zone-1 — never person/clinical identifiers by contract.
      amVisibleDisplayText(safeText(row.memo)),
    ]),
    h("span", { class: "waiting-company", "data-role": "waiting-company" }, [
      companyWords,
    ]),
    addressee,
    h("span", { class: "waiting-age muted", "data-role": "waiting-age" }, [
      formatWaitingAge(row.createdAt),
    ]),
  ]);
  li.append(meta);

  const actions = h("div", { class: "waiting-actions" });
  li.append(actions);

  const paintActions = (): void => {
    clear(actions);
    if (!canReassign(row.assignToRole, row.knownRoles)) return;

    const panel = h("div", {
      class: "reassign-panel",
      "data-role": "reassign-panel",
      hidden: "true",
    });

    const open = button("Reassign…", "quiet", {
      class: "btn-ghost",
      "data-action": "reassign-open",
      "aria-label": "Reassign",
    });

    const roleSelect = h("select", {
      class: "reassign-select field-input",
      "aria-label": "Assign to role",
      "data-role": "reassign-role",
    });
    for (const slug of seedRoleOptions(row.knownRoles)) {
      roleSelect.append(
        h(
          "option",
          {
            value: slug,
            ...(slug === row.assignToRole ? { selected: "selected" } : {}),
          },
          [humanizeRole(slug)],
        ),
      );
    }

    const confirm = button("Confirm", "primary", {
      "data-action": "reassign-confirm",
      "aria-label": "Confirm reassign",
    });
    const cancel = button("Cancel", "quiet", {
      class: "btn-ghost",
      "data-action": "reassign-cancel",
      "aria-label": "Cancel reassign",
    });
    const errLine = h("div", {
      class: "status banner-err",
      "data-role": "reassign-error",
      hidden: "true",
    });

    panel.append(
      h("label", { class: "reassign-label field" }, [
        h("span", { class: "field-label" }, ["Role"]),
        h("span", { class: "select-shell" }, [
          roleSelect,
          h("span", { class: "select-chevron", "aria-hidden": "true" }, [svgIcon("chevron")]),
        ]),
      ]),
      h("div", { class: "actions" }, [confirm, cancel]),
      errLine,
    );

    open.addEventListener("click", () => {
      panel.hidden = false;
      panel.removeAttribute("hidden");
      open.hidden = true;
      open.setAttribute("hidden", "true");
      roleSelect.focus();
    });
    cancel.addEventListener("click", () => {
      panel.hidden = true;
      panel.setAttribute("hidden", "true");
      open.hidden = false;
      open.removeAttribute("hidden");
      clear(errLine);
      errLine.hidden = true;
      errLine.setAttribute("hidden", "true");
    });
    confirm.addEventListener("click", () => {
      void (async () => {
        const nextRole = roleSelect.value;
        if (!nextRole || nextRole === row.assignToRole) {
          cancel.click();
          return;
        }
        // The one write primitive (B2a-5): the click, Enter and any shortcut
        // call one gate, the whole panel is disabled while it runs, and the
        // re-read happens after the write landed (never inside its catch).
        const outcome = await runWrite({
          key: `hub-question-assign:${row.externalRef}`,
          scope: panel,
          gate: () => null,
          send: () =>
            client.assignHubQuestion(row.company, row.externalRef, {
              assignTo: { role: nextRole },
            }),
          afterLanded: () => {
            row.assignToRole = nextRole;
            if (!row.knownRoles.includes(nextRole)) {
              row.knownRoles = [...row.knownRoles, nextRole];
            }
            clear(addressee);
            addressee.append(document.createTextNode(humanizeRole(nextRole)));
            announce(`Reassigned to ${humanizeRole(nextRole)}.`);
            paintActions();
          },
          stillCurrent: opts.stillCurrent,
        });
        if (outcome.kind === "landed") return;
        // Round 11 (R7-06): 409/404 means the question is no longer open —
        // retrying the same request can never succeed, and leaving the row
        // there invites another press. Re-read the list and say so.
        if (
          outcome.error instanceof AdminApiError &&
          (outcome.error.status === 409 || outcome.error.status === 404)
        ) {
          await refreshWithNotice("That question is no longer open.");
          return;
        }
        if (outcome.kind === "refused" && outcome.status === 0) {
          // The gate announced its own reason; nothing was sent.
          return;
        }
        clear(errLine);
        if (outcome.error !== undefined) {
          const copy = failureCopy(outcome.error, { role, context: "write" });
          errLine.append(document.createTextNode(copy.sentence));
          if (copy.detail !== null) {
            errLine.append(
              disclosure("Show details", h("p", {}, [copy.detail])),
            );
          }
        } else {
          errLine.append(document.createTextNode(outcomeSentence(outcome, role)));
        }
        errLine.hidden = false;
        errLine.removeAttribute("hidden");
        confirm.removeAttribute("disabled");
        cancel.removeAttribute("disabled");
      })();
    });

    actions.append(open, panel);
  };

  paintActions();
  return li;
}

function mountPaperworkHeader(
  title: string,
  titleRole: string,
  lede: string,
  ledeRole: string,
): HTMLElement {
  const header = pageHeader(title, lede);
  header.querySelector("h1")?.setAttribute("data-role", titleRole);
  header.querySelector("p")?.setAttribute("data-role", ledeRole);
  return header;
}

function paintWaitingLoadFailure(
  root: HTMLElement,
  opts: WaitingPageOptions,
  role: StaffRole,
  error?: unknown,
): void {
  clear(root);
  const retry = button("Try again", "primary", {
    "data-action": "retry",
    "aria-label": "Try again",
  });
  retry.addEventListener("click", () => {
    // A retry re-reads the page; it is not the action that left a notice
    // (round 2, MUST-FIX 1).
    void renderWaitingPage({ ...opts, notice: undefined });
  });
  // Round 3, MUST-FIX 2: a rate-limited load says the same sentence Months and
  // the searches use, so "Try again" does not read as a server fault.
  const copy =
    error instanceof AdminApiError && error.status === 429
      ? failureCopy(error, { role, context: "read" }).sentence
      : "Couldn't load questions waiting on other people. Try again.";
  root.append(
    mountPaperworkHeader(
      "Waiting",
      "waiting-title",
      "Questions that belong to someone else. Oldest first.",
      "waiting-lede",
    ),
    h("div", { class: "error-state", role: "alert", "data-role": "error-state" }, [
      h("p", {}, [copy]),
      retry,
    ]),
  );
}

/** `2026-10-07` -> `Oct 7, 2026`; anything unreadable is shown unchanged. */
function formatDisplayDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match === null) return value;
  const date = new Date(`${match[1] ?? ""}-${match[2] ?? ""}-${match[3] ?? ""}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

async function loadActiveCompanies(client: AdminClient): Promise<Company[]> {
  const { companies } = await client.listCompanies();
  return companies.filter((c) => c.isActive);
}

async function loadHubLinkedCompanies(client: AdminClient): Promise<Company[]> {
  // Non-hub → never probed for hub questions (SCOPE + §4.3). API would return
  // empty; skip the call.
  return (await loadActiveCompanies(client)).filter((c) => c.hubLink !== null);
}

/** Relative waiting age from createdAt. */
export function formatWaitingAge(createdAt: string, now = new Date()): string {
  const ms = Math.max(0, now.getTime() - new Date(createdAt).getTime());
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${String(minutes)}m waiting`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${String(hours)}h waiting`;
  const days = Math.floor(hours / 24);
  return `${String(days)}d waiting`;
}

function formatAmount(amount: string): string {
  return moneyText(amount, { fallback: "No amount" });
}

/**
 * Count open hub questions across hub-linked companies (AM reward gate).
 * Returns null when any call fails — caller must not claim "Nothing needs you."
 */
export async function countOpenHubQuestions(client: AdminClient): Promise<number | null> {
  const companies = await loadHubLinkedCompanies(client);
  const results = await mapWithConcurrency(companies, FANOUT_CONCURRENCY, async (company) => {
    try {
      const body = await client.listHubQuestions(company.slug, { status: "open" });
      return body.questions.length;
    } catch {
      return null;
    }
  });
  if (results.some((n) => n === null)) return null;
  return results.reduce<number>((sum, n) => sum + (n ?? 0), 0);
}
